/**
 * @file csvReportExporter.js
 * @description Shared CSV report exporter for normalizing JSON-to-CSV report generation via a reusable definition interface.
 */

import { LogEventType } from '../data/enums';
import { logEvent } from '../../services/loggingService';

/**
 * @typedef {Object} CsvReportDefinition
 * @property {string[]} [headers]
 * @property {string} filenamePrefix
 * @property {(queryParams?: any) => Promise<any>} getData
 * @property {(payload: any) => Array<any>} [selectRows]
 * @property {(row: any) => Object} [mapRow]
 * @property {(a: Object, b: Object) => number} [sortRows]
 * @property {(dateStamp: string, queryParams?: any) => string} [filenameBuilder]
 * @property {boolean} [addTextQualifier]
 * @property {(key: string) => string} [mapHeader]
 * @property {boolean} [flattenRows]
 */

function identity(value) {
    return value;
}

function selectArrayPayload(payload) {
    if (Array.isArray(payload)) {
        return payload;
    }

    return [];
}

function isFunction(value) {
    return typeof value === 'function';
}

function capitalizeWord(word) {
    if (!word) {
        return word;
    }

    return `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
}

function formatHeaderFromKey(key) {
    return `${key}`
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([a-zA-Z])([0-9])/g, '$1 $2')
        .replace(/([0-9])([a-zA-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .split(' ')
        .map(capitalizeWord)
        .join(' ');
}

function getDateStamp() {
    const now = new Date();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const day = `${now.getDate()}`.padStart(2, '0');
    const year = now.getFullYear();

    return `${year}-${month}-${day}`;
}

function buildFilename(filenamePrefix) {
    return `${filenamePrefix}${getDateStamp()}.csv`;
}

function mapRows(rows, mapRow, flatten = false) {
    if (flatten) {
        return rows.flatMap(mapRow);
    }

    return rows.map(mapRow);
}

function sortMappedRows(rows, sortRows) {
    if (!sortRows) {
        return rows;
    }

    return [...rows].sort(sortRows);
}

function resolveFilename(reportDefinition, queryParams) {
    if (isFunction(reportDefinition.filenameBuilder)) {
        return reportDefinition.filenameBuilder(getDateStamp(), queryParams);
    }

    return buildFilename(reportDefinition.filenamePrefix);
}

function validateReportDefinition(reportDefinition) {
    if (!reportDefinition || typeof reportDefinition !== 'object') {
        throw new Error('CsvReportDefinition must be an object.');
    }

    if (
        'headers' in reportDefinition &&
        !Array.isArray(reportDefinition.headers)
    ) {
        throw new Error(
            'CsvReportDefinition.headers must be an array when provided.',
        );
    }

    if (!isFunction(reportDefinition.getData)) {
        throw new Error('CsvReportDefinition.getData must be a function.');
    }

    if (
        !isFunction(reportDefinition.filenameBuilder) &&
        typeof reportDefinition.filenamePrefix !== 'string'
    ) {
        throw new Error(
            'CsvReportDefinition.filenamePrefix must be a string when filenameBuilder is not provided.',
        );
    }
}

function getHeadersFromRows(rows, mapHeader) {
    if (!rows.length) {
        return [];
    }

    const [firstRow] = rows;
    const keys = Object.keys(firstRow);

    if (!isFunction(mapHeader)) {
        return keys;
    }

    return keys.map(mapHeader);
}

export function createCsvReportDefinition(reportDefinition) {
    validateReportDefinition(reportDefinition);

    return {
        ...reportDefinition,
        selectRows: reportDefinition.selectRows || selectArrayPayload,
        mapRow: reportDefinition.mapRow || identity,
        flattenRows: reportDefinition.flattenRows || false,
        addTextQualifier:
            'addTextQualifier' in reportDefinition
                ? reportDefinition.addTextQualifier
                : true,
        mapHeader: reportDefinition.mapHeader || formatHeaderFromKey,
    };
}

export function downloadCSVWithCustomHeaders(
    jsonPayload,
    filename,
    headers,
    addTextQualifier = true,
) {
    const allData = buildCsvContent(jsonPayload, headers, addTextQualifier);
    const blob = new Blob([allData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download === undefined) {
        return;
    }

    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    logEvent(LogEventType.DataDownloaded, `Report downloaded: ${filename}`);
}

export function buildCsvContent(rows, headers, addTextQualifier = true) {
    const csvRows = addTextQualifier
        ? rows.map(row => `"${Object.values(row).join('","')}"`)
        : rows.map(row => Object.values(row).join(','));

    return [headers, ...csvRows].join('\n');
}

export async function buildCsvReportData(reportDefinition, queryParams) {
    const finalizedDefinition = createCsvReportDefinition(reportDefinition);
    const payload = await finalizedDefinition.getData(queryParams);
    const rows = finalizedDefinition.selectRows(payload);
    const mappedRows = mapRows(
        rows,
        finalizedDefinition.mapRow,
        finalizedDefinition.flattenRows,
    );
    const sortedRows = sortMappedRows(mappedRows, finalizedDefinition.sortRows);
    const filename = resolveFilename(finalizedDefinition, queryParams);
    const headers =
        finalizedDefinition.headers ||
        getHeadersFromRows(sortedRows, finalizedDefinition.mapHeader);

    return {
        filename,
        rows: sortedRows,
        headers,
        addTextQualifier: finalizedDefinition.addTextQualifier,
    };
}

export async function exportCsvReport(reportDefinition, queryParams) {
    const reportData = await buildCsvReportData(reportDefinition, queryParams);

    downloadCSVWithCustomHeaders(
        reportData.rows,
        reportData.filename,
        reportData.headers,
        reportData.addTextQualifier,
    );
}
